import CodeMirror from "codemirror/lib/codemirror";
import { unescape } from "lodash";
import Formula from "~/models/formula";
import { formulaFunctions } from "../formulaFunctions";
import { IDynamicReferencedThing } from "../interfaces/dynamicFieldsInterfaces";
import { IFormulaFunction, IFormulaFunctionArgument } from "../interfaces/formulaInterfaces";
import { Hash } from "../interfaces/hashInterfaces";
import cTooltip from "../modules/cTooltip/tooltipManager";
import CodeMirrorEditor from "./codeMirrorEditor";
import Guid from "./guid";

export enum WidgetType {
  FormulaFunctionWidget = 1,
  FormFieldWidget,
  MentionWidget,
}

export interface ICursorPosition {
  line: number
  ch: number
}

interface ICursorPositionFromTo {
  from: ICursorPosition
  to: ICursorPosition
}

/**
 * A `Widget` is a unit of the CodeMirrorEditor
 * A Mark is a 'marked up' piece of the html that can
 * have structure and events applied to it
 */

export abstract class CodeMirrorWidget {
  widgetType: WidgetType;
  editor: CodeMirrorEditor;
  marks: any[] = [];
  element: HTMLElement = null;
  guid: string;

  protected constructor(codeMirrorEditor) {
    this.editor = codeMirrorEditor;
    this.guid = Guid.create();
  }

  protected posEq(posA, posB): boolean {
    return posA.line === posB.line && posA.ch === posB.ch
  }

  get codeMirror(): CodeMirror {
    return this.editor.codeMirror
  }

  protected getCursor(): ICursorPositionFromTo {
    const from = this.codeMirror.getCursor('from');
    const to = this.codeMirror.getCursor('to');
    return {from, to};
  }

  protected insertText(text: string): void {
    const {from, to} = this.getCursor();
    this.codeMirror.replaceRange(text, from, to);
  }

  protected insertTextAt(text: string, from: ICursorPosition, to: ICursorPosition) {
    this.codeMirror.replaceRange(text, from, to);
  }

  protected insertNode(node: HTMLElement, delimiter: string = ''): void {
    this.insertText(delimiter + node.dataset.replaceText + delimiter);
    const {from, to} = this.getCursor();
    from.ch = from.ch - node.dataset.replaceText.length - (delimiter.length * 2);
    const mark = this.codeMirror.markText(from, to, {
      replacedWith: node,
      clearWhenEmpty: true,
    });
    mark.replacedWith.dataset.markId = mark.id;
    mark.replacedWith.dataset.guid = this.guid;

    this.marks.push(mark);
    this.codeMirror.setCursor(to);
    this.codeMirror.focus();
  }

  selectChipListener(e) {
    const $target = $(e.target);
    if (e.target.tagName === 'VAR') {
      $target.trigger('chip:selected');

      // sets caret after selected dynamic field
      const sel = window.getSelection();
      let range = document.createRange();
      let node: Node = sel.focusNode?.parentNode?.nextSibling;
      let offset = 0;
      // if selected dynamic field is end of line
      if (!node) {
        node = sel.focusNode.parentNode.parentNode;
        offset = node.childNodes.length;
      }
      range.setStart(node, offset);
      range.setEnd(node, offset);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  createFormFieldChip(data: IDynamicReferencedThing): HTMLElement {
    const el = CodeMirrorWidget.createVarChip(data);
    el.addEventListener('click', this.selectChipListener.bind(this));
    return el;
  }

  static createVarChip(data) {
    const el = document.createElement('var');
    el.className = `pill ${data?.pillColor || 'light-grey'} formula-pill `;
    el.setAttribute('style', "cursor:default;");
    el.contentEditable = 'false';
    if (data) {
      el.dataset.replaceText = Formula.buildDynamicId({prefix: Formula.DYNAMIC_PREFIX, id: Formula.clientIdToFormulaId(data.id)});
      if (data.wsTitle) {
        el.dataset.cTooltip = `${data.wsTitle} > ${data.label}`;
        delete data.wsTitle;
      }
      el.innerText = unescape(data.label);
      delete data.label;
      el.dataset.dynamic = JSON.stringify(Formula.sliceDynamicDataset(data));
    }
    return el
  }
}

export class FormulaFunctionWidget extends CodeMirrorWidget {
  function: IFormulaFunction;
  widgetType: WidgetType.FormulaFunctionWidget;

  constructor(editor: CodeMirrorEditor, opts: Hash) {
    super(editor);
    this.function = formulaFunctions.find(formFunc => formFunc.name === opts.name);
    this.insertFunction();
    new cTooltip().init();
  }

  private insertFunction(): void {
    this.insertText(`${this.function.name}(`);
    this.function.args.forEach((arg, i) => {
      const argNode = this.generateArgNode(arg);
      argNode.addEventListener('click', this.selectChipListener.bind(this));
      argNode.setAttribute("style", "cursor:default;");
      this.insertNode(argNode, "'");
      if (this.function.args[i + 1]) this.insertText(', ')
    });
    this.insertText(')');
  }

  private generateArgNode(arg: IFormulaFunctionArgument): HTMLElement {
    const node = document.createElement('var');
    node.className = 'pill noselect formula-pill';
    node.contentEditable = 'false';
    node.dataset.replaceText = arg.type;
    node.dataset.cTooltip = arg.description;
    if (arg.required) {
      node.innerText = `${arg.placeholder}`
    } else if (arg.repeatable) {
      node.innerText = `[${arg.placeholder}], ...`
    } else {
      node.innerText = `[${arg.placeholder}]`
    }
    return node;
  }
}

export class FormFieldWidget extends CodeMirrorWidget {
  widgetType: WidgetType.FormFieldWidget;
  data: IDynamicReferencedThing;

  constructor(editor: CodeMirrorEditor, opts: IDynamicReferencedThing) {
    super(editor);
    this.data = opts;
    this.element = this.createFormFieldChip(this.data);
    this.insertNode(this.element);
    new cTooltip().init($(this.element.parentElement));
  }
}

export interface ICompletionObject extends ICursorPositionFromTo {
  list: Array<ICompletion>
}

export interface ICompletion {
  text: string
  displayText: string
  render: Function
  initials: string
  avatarId: string
}

export interface ICompletionQuery extends ICursorPositionFromTo {
  query: string
}

export interface ICustomHintOptions {
  hint: Function,
  closeCharacters: any
  completeSingle: boolean
  className: string
  organizationId: string
}

/**
 * This class relies on CodeMirror and an addon for CodeMirror called show-hint.js.
 * @see {@link https://codemirror.net/doc/manual.html#addon_show-hint}
 */
export class MentionWidget extends CodeMirrorWidget {
  widgetType: WidgetType.MentionWidget
  data: ICompletion

  constructor(editor: CodeMirrorEditor, opts: ICompletion) {
    super(editor)
    this.data = opts
  }
  /**
   * Creates the node that is appended to the popup container and that represents an available completion.
   * This function is passed to the render option of the 'completion object'.
   * @see {@link https://codemirror.net/doc/manual.html#addon_show-hint}
   */
  static generateListItemEl(containerEl: HTMLElement, completionObject: ICompletionObject, completion: ICompletion): void {
    const lineItem = document.createElement('var')
    if (completion.avatarId) {
      const url = `url(/account/image_uploads/${completion.avatarId})`
      lineItem.innerHTML = `
        <div class="dropdown-item mention-item">
        <span class="avatar xs" style="{'background-image': ${url}"></span><div class="avatar-title">@${completion.displayText}</div>
        </div>
      `
    } else {
      lineItem.innerHTML = `
        <div class="dropdown-item mention-item">
        <span class="avatar xs">${completion.initials}</span><div class="avatar-title">@${completion.displayText}</div>
        </div>
      `
    }
    containerEl.append(lineItem)
  }

  /**
   * Upon close of popup, style and insert mention into CodeMirror by
   * traversing back to '@' and replacing this range with mention node
   */
  mark(): void {
    const cursor = this.editor.codeMirror.getCursor()
    const lineText: string = this.editor.codeMirror.getLine(cursor.line)
    let start: number = cursor.ch
    let end: number = start
    while (start && !(/@/.test(lineText.charAt(start)))) { --start }

    const el = this.generateMentionEl()
    const from = CodeMirror.Pos(cursor.line, start)
    const to = CodeMirror.Pos(cursor.line, end)
    const position = {from, to}
    this.insertMentionEl(el, position)
  }

  generateMentionEl(): HTMLElement {
    const node = document.createElement('var')
    node.contentEditable = 'false'
    node.innerText = `@${this.data.displayText}`
    node.className = 'mention'
    return node
  }

  insertMentionEl(node: HTMLElement, position: ICursorPositionFromTo): void {
    const mark = this.codeMirror.markText(position.from, position.to, {
      replacedWith: node,
      clearWhenEmpty: true,
    });
    mark.replacedWith.dataset.markId = mark.id
    mark.replacedWith.dataset.guid = this.guid

    this.marks.push(mark)
    this.codeMirror.setCursor(position.to)
    this.codeMirror.focus()
  }
}